import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import { Card, Button, Input } from './StyledComponents';
import { useAuctions } from '../hooks/useAuctions';
import { CONTRACTS, ABIS } from '../utils/contracts';
import { txOverrides } from '../utils/tx';
import { formatNumber, truncateAddress } from '../utils/formatters';

// ---------------------------------------------------------------------------
// Auctions tab — the public window onto all three auction venues:
//   · Collateral (Dutch): liquidated vaults' PLS/WPLS sold for JDAI
//   · Surplus (English): system profits sold for TKR, which is burned
//   · Debt (English): freshly minted TKR sold to cover bad debt
// Readable without a wallet. Bidding works once connected. While the system is
// deliberately COLD (nothing armed), this page says so honestly instead of
// pretending activity.
// ---------------------------------------------------------------------------

const Wrap = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Intro = styled.div`
  text-align: center;
  padding: 1rem 0 0.5rem;
  h2 { color: #ffd700; margin: 0 0 0.5rem; }
  p { color: #ccc; max-width: 680px; margin: 0 auto; line-height: 1.6; }
`;

const StatusRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
`;

const Pill = styled.div`
  background: #181818;
  border: 1px solid #2a2a2a;
  border-radius: 10px;
  padding: 0.8rem 1rem;
  .k { font-size: 0.68rem; letter-spacing: 0.08em; text-transform: uppercase; color: #777; }
  .v { margin-top: 0.25rem; font-size: 1.05rem; font-weight: 600; color: #eee; font-variant-numeric: tabular-nums; }
  .v.green { color: #10b981; }
  .v.red { color: #ef4444; }
`;

const SectionTitle = styled.h3`
  color: #ffd700; margin: 0 0 0.3rem; font-size: 1.15rem;
`;
const SectionBlurb = styled.p`
  color: #999; font-size: 0.85rem; margin: 0 0 1rem; line-height: 1.5;
`;

const Quiet = styled.div`
  color: #888; font-size: 0.9rem; padding: 0.9rem 1rem;
  background: #161616; border: 1px dashed #333; border-radius: 8px; line-height: 1.55;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: center;
  padding: 0.9rem 0;
  border-top: 1px solid #222;
  &:first-of-type { border-top: none; }
  .info { font-size: 0.9rem; color: #ccc; line-height: 1.6; font-variant-numeric: tabular-nums; }
  .info .id { color: #ffd700; font-weight: 600; }
  .info .dim { color: #777; }
  .actions { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }
  @media (max-width: 620px) { grid-template-columns: 1fr; }
`;

// Slow attention pulse — used to point the eye at the deposit box when the user
// has a live auction to bid on but no internal balance to bid with yet.
const glow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 215, 0, 0); border-color: #ffd700; }
  50%      { box-shadow: 0 0 0 6px rgba(255, 215, 0, 0.28); border-color: #ffe873; }
`;
const Mini = styled(Input).withConfig({ shouldForwardProp: (p) => p !== 'pulse' })`
  width: 110px; padding: 0.45rem 0.6rem; font-size: 0.85rem;
  ${(p) => p.pulse && css`animation: ${glow} 1.8s ease-in-out infinite;`}
`;

const pulseBtn = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 215, 0, 0); }
  50%      { box-shadow: 0 0 0 5px rgba(255, 215, 0, 0.30); }
`;
const PulseButton = styled(Button)`
  ${(p) => p.$pulse && css`animation: ${pulseBtn} 1.8s ease-in-out infinite;`}
`;

const Hint = styled.span`
  color: #ffd700; font-size: 0.82rem; font-weight: 600;
  display: inline-flex; align-items: center; gap: 0.35rem;
`;

const nowSec = () => Math.floor(Date.now() / 1000);
const countdown = (t) => {
  const s = t - nowSec();
  if (s <= 0) return 'ended';
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m ${s % 60}s`;
};
// English auction settle-ability: a bid exists and its bid-timer or the
// auction's end has passed.
const finished = (a) => a.tic !== 0 && (a.tic < nowSec() || a.end < nowSec());
// Expired with zero bids: tend and deal both revert; only tick() reopens it.
const expiredNoBids = (a) => a.tic === 0 && a.end < nowSec();

const AuctionsPage = ({ signer, account }) => {
  const a = useAuctions(account);
  const [inputs, setInputs] = useState({});
  const setIn = (k, v) => setInputs((s) => ({ ...s, [k]: v }));

  // fn receives a `step` callback so multi-signature flows can tell the user
  // exactly which wallet prompt they're on ("Sign 1 of 2 — …").
  const act = async (label, fn) => {
    toast.loading(label, { id: 'auction' });
    try {
      await fn((m) => toast.loading(m, { id: 'auction' }));
      toast.success('Done!', { id: 'auction' });
      a.refresh();
    } catch (e) {
      toast.error(`${label} failed: ${e?.reason || e?.shortMessage || e?.message || 'unknown'}`,
        { id: 'auction', duration: 8000 });
    }
  };
  const c = (addr, abi) => new ethers.Contract(addr, abi, signer);
  const ov = () => txOverrides(signer.provider);

  // ---- bidding wallet helpers ----
  const depositJdai = (amt) => act(`Depositing ${amt} JDAI for bidding`, async (step) => {
    const wad = ethers.parseEther(amt);
    const jdai = c(CONTRACTS.JDAI, ABIS.JDAI);
    const needApprove = (await jdai.allowance(account, CONTRACTS.DAIJOIN)) < wad;
    if (needApprove) {
      step('Sign 1 of 2 — one-time JDAI approval…');
      await (await jdai.approve(CONTRACTS.DAIJOIN, ethers.MaxUint256, await ov())).wait();
    }
    step(needApprove ? 'Sign 2 of 2 — depositing…' : 'Sign 1 of 1 — depositing…');
    await (await c(CONTRACTS.DAIJOIN, ABIS.DAIJOIN).join(account, wad, await ov())).wait();
  });
  const withdrawJdai = (amt) => act(`Withdrawing ${amt} JDAI to wallet`, async (step) => {
    const wad = ethers.parseEther(amt);
    const vat = c(CONTRACTS.VAT, ABIS.VAT);
    const needHope = (await vat.can(account, CONTRACTS.DAIJOIN)) === 0n;
    if (needHope) {
      step('Sign 1 of 2 — one-time withdrawal authorization…');
      await (await vat.hope(CONTRACTS.DAIJOIN, await ov())).wait();
    }
    step(needHope ? 'Sign 2 of 2 — withdrawing…' : 'Sign 1 of 1 — withdrawing…');
    await (await c(CONTRACTS.DAIJOIN, ABIS.DAIJOIN).exit(account, wad, await ov())).wait();
  });

  // ---- collateral (Dutch) ----
  const takeAll = (c2, auc) => act(`Buying auction #${auc.id} (${c2.name})`, async (step) => {
    const vat = c(CONTRACTS.VAT, ABIS.VAT);
    const needHope = !a.user?.hoped[c2.name];
    // hope? + take + withdraw + (unwrap, for wrapped collateral)
    const total = (needHope ? 1 : 0) + 2 + (c2.name === 'PLS-A' ? 0 : 1);
    let n = 0;
    if (needHope) {
      step(`Sign ${++n} of ${total} — one-time authorization for this auction house…`);
      await (await vat.hope(c2.clip, await ov())).wait();
    }
    // whole lot at up to the current price (fresh read at click time — the
    // Dutch price only falls, so this max can't be beaten upward)
    const clip = c(c2.clip, ABIS.CLIPPER);
    const st = await clip.getStatus(auc.id);
    step(`Sign ${++n} of ${total} — buying the lot…`);
    await (await clip.take(auc.id, st.lot, st.price, account, '0x', await ov())).wait();
    // take pays out INTERNAL vat gem — exit it to the wallet, and for WPLS
    // unwrap too, so the buyer ends with native PLS, not a ledger entry
    const gemBal = await vat.gem(c2.ilk, account);
    if (gemBal > 0n) {
      if (c2.name === 'PLS-A') {
        step(`Sign ${++n} of ${total} — withdrawing the collateral to your wallet…`);
        await (await c(CONTRACTS.ETHJOIN, ABIS.ETHJOIN).exit(account, gemBal, await ov())).wait();
      } else {
        step(`Sign ${++n} of ${total} — withdrawing the collateral…`);
        await (await c(CONTRACTS.GEMJOIN_WPLS_A, ABIS.GEMJOIN).exit(account, gemBal, await ov())).wait();
        step(`Sign ${++n} of ${total} — unwrapping to native PLS…`);
        const wbal = await c(CONTRACTS.PLS, ABIS.PLS).balanceOf(account);
        await (await c(CONTRACTS.PLS, ABIS.PLS).withdraw(wbal, await ov())).wait();
      }
    }
  });
  const redo = (clipAddr, auc) => act(`Restarting auction #${auc.id}`, async () => {
    await (await c(clipAddr, ABIS.CLIPPER).redo(auc.id, account, await ov())).wait();
  });

  // ---- surplus (flap) ----
  const tend = (auc) => act(`Bidding TKR on surplus auction #${auc.id}`, async (step) => {
    const bid = ethers.parseEther(inputs[`flap${auc.id}`] || '0');
    const tkr = c(CONTRACTS.TKR, ABIS.TKR);
    const needApprove = !a.user?.flapApproved;
    if (needApprove) {
      step('Sign 1 of 2 — one-time TKR approval…');
      await (await tkr.approve(CONTRACTS.FLAPPER, ethers.MaxUint256, await ov())).wait();
    }
    step(needApprove ? 'Sign 2 of 2 — placing the bid…' : 'Sign 1 of 1 — placing the bid…');
    await (await c(CONTRACTS.FLAPPER, ABIS.FLAPPER).tend(auc.id, auc.lot, bid, await ov())).wait();
  });
  const flapDeal = (auc) => act(`Settling surplus auction #${auc.id}`, async () => {
    await (await c(CONTRACTS.FLAPPER, ABIS.FLAPPER).deal(auc.id, await ov())).wait();
  });
  // expired with zero bids: only tick() reopens it (tend/deal both revert)
  const tick = (which, auc) => act(`Reopening auction #${auc.id}`, async () => {
    const addr = which === 'flap' ? CONTRACTS.FLAPPER : CONTRACTS.FLOPPER;
    const abi = which === 'flap' ? ABIS.FLAPPER : ABIS.FLOPPER;
    await (await c(addr, abi).tick(auc.id, await ov())).wait();
  });
  const kickFlap = () => act('Starting a surplus auction', async (step) => {
    const vow = c(CONTRACTS.VOW, ABIS.VOW_AUCTION);
    // flap requires zero unqueued bad debt — reconcile first if any exists
    const unq = a.vow.unqueuedRaw, dai = a.vow.surplusRaw;
    const needHeal = unq > 0n && dai > 0n;
    if (needHeal) {
      step('Sign 1 of 2 — reconciling the books (heal) first…');
      await (await vow.heal(unq < dai ? unq : dai, await ov())).wait();
    }
    step(needHeal ? 'Sign 2 of 2 — starting the auction…' : 'Sign 1 of 1 — starting the auction…');
    await (await vow.flap(await ov())).wait();
  });

  // ---- debt (flop) ----
  const dent = (auc) => act(`Bidding on debt auction #${auc.id}`, async (step) => {
    const lot = ethers.parseEther(inputs[`flop${auc.id}`] || '0');
    const vat = c(CONTRACTS.VAT, ABIS.VAT);
    const needHope = !a.user?.hoped.flop;
    if (needHope) {
      step('Sign 1 of 2 — one-time authorization for the debt-auction house…');
      await (await vat.hope(CONTRACTS.FLOPPER, await ov())).wait();
    }
    step(needHope ? 'Sign 2 of 2 — placing the bid…' : 'Sign 1 of 1 — placing the bid…');
    await (await c(CONTRACTS.FLOPPER, ABIS.FLOPPER).dent(auc.id, lot, auc.bid, await ov())).wait();
  });
  const flopDeal = (auc) => act(`Settling debt auction #${auc.id}`, async () => {
    await (await c(CONTRACTS.FLOPPER, ABIS.FLOPPER).deal(auc.id, await ov())).wait();
  });
  const kickFlop = () => act('Starting a debt auction', async (step) => {
    const vow = c(CONTRACTS.VOW, ABIS.VOW_AUCTION);
    // flop requires ZERO surplus — net it against bad debt first if any remains
    const unq = a.vow.unqueuedRaw, dai = a.vow.surplusRaw;
    const needHeal = dai > 0n && unq > 0n;
    if (needHeal) {
      step('Sign 1 of 2 — reconciling the books (heal) first…');
      await (await vow.heal(dai < unq ? dai : unq, await ov())).wait();
    }
    step(needHeal ? 'Sign 2 of 2 — starting the auction…' : 'Sign 1 of 1 — starting the auction…');
    await (await vow.flop(await ov())).wait();
  });
  // QA/operator housekeeping: net surplus against bad debt. The keeper bot does
  // this automatically in active mode; this button is the manual fallback.
  const healNow = () => act('Reconciling the books (heal)', async () => {
    const unq = a.vow.unqueuedRaw, dai = a.vow.surplusRaw;
    await (await c(CONTRACTS.VOW, ABIS.VOW_AUCTION).heal(unq < dai ? unq : dai, await ov())).wait();
  });

  if (a.isLoading) {
    return <Wrap><Card style={{ textAlign: 'center', color: '#888' }}>Reading auction state from the chain…</Card></Wrap>;
  }
  // Only blank the page when we have never loaded — a transient RPC failure on
  // a later 30s poll keeps showing the last good data instead.
  if (a.error && !a.collateral) {
    return <Wrap><Card style={{ textAlign: 'center', color: '#ef4444' }}>Could not read auction state: {a.error}</Card></Wrap>;
  }

  const connectHint = !account && (
    <p style={{ fontSize: '0.8rem', color: '#777', margin: '0.75rem 0 0' }}>
      Connect a wallet to bid. Reading needs nothing.
    </p>
  );

  // Guidance: there's a collateral auction you could take, but your internal
  // balance can't cover even the cheapest one yet → light up the deposit box.
  // (Guard on "can't afford", not "exactly zero" — a dust balance still needs
  // topping up, and rounds to 0 in the display anyway.)
  const takeableCosts = a.collateral.flatMap((c2) =>
    c2.auctions.filter((x) => !x.needsRedo).map((x) => Math.min(x.lot * x.price, x.tab)));
  const cheapestTake = takeableCosts.length ? Math.min(...takeableCosts) : Infinity;
  const needsDeposit = !!a.user && takeableCosts.length > 0 && a.user.vatDaiNum < cheapestTake;

  return (
    <Wrap>
      <Intro>
        <h2>Auctions</h2>
        <p>
          Where the system enforces itself: liquidated collateral is sold for JDAI,
          surplus profits buy and burn TKR, and bad debt mints TKR to recapitalize.
          Everything here is permissionless — any address can bid, settle, or restart
          an auction, and every button below maps to one public contract call.
        </p>
      </Intro>

      {/* ------------------------------ status ------------------------------ */}
      <StatusRow>
        <Pill>
          <div className="k">Liquidations</div>
          <div className={`v ${a.armed ? 'red' : 'green'}`}>{a.armed ? 'ACTIVE' : 'Installed, not active'}</div>
        </Pill>
        <Pill>
          <div className="k">Surplus auctions</div>
          <div className={`v ${a.vow.humpCold ? 'green' : a.flap.reachable ? 'red' : ''}`}>
            {a.vow.humpCold ? 'Installed, not active' : a.flap.reachable ? 'KICKABLE NOW' : 'Below trigger'}
          </div>
        </Pill>
        <Pill>
          <div className="k">System surplus</div>
          <div className="v">{formatNumber(a.vow.surplus, 2)} JDAI</div>
        </Pill>
        <Pill>
          <div className="k">Bad debt (sin)</div>
          <div className="v">{formatNumber(a.vow.sinTotal, 2)} JDAI</div>
        </Pill>
        <Pill>
          <div className="k">TKR supply</div>
          <div className="v">{formatNumber(a.tkrSupply, 0)}</div>
        </Pill>
      </StatusRow>

      {/* Operator housekeeping: surplus and bad debt coexisting just means
          nobody has netted them yet. The keeper bot does this automatically in
          active mode; this is the manual control (page is QA-gated anyway). */}
      {account && a.vow.surplusRaw > 0n && a.vow.unqueuedRaw > 0n && (
        <Quiet style={{ display: 'flex', alignItems: 'center', gap: '1rem', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <span>
            {formatNumber(a.vow.surplus, 2)} JDAI of surplus and {formatNumber(a.vow.unqueued, 2)} JDAI
            of reconcilable bad debt are both on the books — netting them cancels {formatNumber(Math.min(a.vow.surplus, a.vow.unqueued), 2)} JDAI of each.
          </span>
          <Button variant="secondary" onClick={healNow}>Reconcile books (heal)</Button>
        </Quiet>
      )}

      {/* -------------------------- bidding wallet -------------------------- */}
      {account && a.user && (
        <Card>
          <SectionTitle>Your bidding balances</SectionTitle>
          <SectionBlurb>
            Collateral and debt auctions are paid from an internal JDAI balance inside the
            vault engine — deposit JDAI here first, withdraw it back to your wallet any time.
            Surplus auctions are bid in TKR straight from your wallet. Your first-ever
            deposit or withdrawal takes two signatures (a one-time approval comes first).
          </SectionBlurb>
          <Row>
            <div className="info">
              Internal JDAI: <strong>{formatNumber(a.user.vatDaiNum, 4)}</strong>
              <span className="dim"> · wallet JDAI: {formatNumber(a.user.walletJdaiNum, 4)}</span>
              <span className="dim"> · wallet TKR: {formatNumber(a.user.tkrBalNum, 2)}</span>
              {needsDeposit && (
                <div style={{ marginTop: '0.4rem' }}>
                  <Hint>→ Deposit JDAI here to bid on the auction below</Hint>
                </div>
              )}
            </div>
            <div className="actions">
              <Mini placeholder="amount" pulse={needsDeposit && !inputs.bank}
                value={inputs.bank || ''} onChange={(e) => setIn('bank', e.target.value)} />
              <PulseButton $pulse={needsDeposit && !!inputs.bank}
                onClick={() => depositJdai(inputs.bank || '0')} disabled={!inputs.bank}>Deposit</PulseButton>
              <Button variant="secondary" onClick={() => withdrawJdai(inputs.bank || '0')} disabled={!inputs.bank}>Withdraw</Button>
            </div>
          </Row>
        </Card>
      )}

      {/* ------------------------ collateral auctions ----------------------- */}
      <Card>
        <SectionTitle>Collateral auctions</SectionTitle>
        <SectionBlurb>
          When a vault falls below its minimum, anyone can trigger it into a Dutch auction:
          the price starts 20% above oracle and falls ~1% every 90 seconds until a buyer
          takes the collateral for JDAI. Triggering pays a bounty (0.05 JDAI + 2% of the debt).
          Buying takes two to three quick signatures — the purchase, withdrawing the
          collateral, and (for wrapped collateral) unwrapping it so you end with native
          PLS in your wallet. First-ever buy adds a one-time authorization.
        </SectionBlurb>
        {a.collateral.every((c2) => c2.auctions.length === 0) ? (
          <Quiet>
            No collateral auctions — liquidations are {a.armed ? 'active, but every vault is currently safe'
              : 'installed but deliberately switched off (every liquidation ceiling is zero), so none can start. Activation will be announced in advance'}.
          </Quiet>
        ) : (
          a.collateral.map((c2) => c2.auctions.map((auc) => (
            <Row key={`${c2.name}${auc.id}`}>
              <div className="info">
                <span className="id">{c2.name} #{auc.id}</span>{' '}
                {formatNumber(auc.lot, 0)} {c2.gem} for {formatNumber(auc.tab, 2)} JDAI owed
                {' '}· current price {auc.price.toPrecision(4)} JDAI/{c2.gem}
                {' '}· vault {truncateAddress(auc.usr)}
                {/* The actionable numbers: what you pay right now for the whole
                    lot (capped at the tab) and how much collateral that buys. */}
                {(() => {
                  const cost = Math.min(auc.lot * auc.price, auc.tab);
                  const receive = auc.price > 0 ? cost / auc.price : 0;
                  return (
                    <div style={{ marginTop: '0.35rem', color: '#ffd700', fontWeight: 600 }}>
                      Buy the whole lot now: pay ~{formatNumber(cost, 2)} JDAI → receive ~{formatNumber(receive, 0)} {c2.gem}
                    </div>
                  );
                })()}
                {auc.needsRedo && <span style={{ color: '#f59e0b' }}> · expired — restart for the bounty</span>}
              </div>
              <div className="actions">
                {account ? (
                  auc.needsRedo ? (
                    <Button onClick={() => redo(c2.clip, auc)}>Restart (earn bounty)</Button>
                  ) : (
                    // the contract caps what a whole-lot take costs at the tab
                    <PulseButton onClick={() => takeAll(c2, auc)}
                      $pulse={!!a.user && a.user.vatDaiNum >= Math.min(auc.lot * auc.price, auc.tab)}
                      disabled={!a.user || a.user.vatDaiNum < Math.min(auc.lot * auc.price, auc.tab)}>
                      Buy whole lot (~{formatNumber(Math.min(auc.lot * auc.price, auc.tab), 2)} JDAI)
                    </PulseButton>
                  )
                ) : <span style={{ color: '#777', fontSize: '0.8rem' }}>connect to bid</span>}
              </div>
            </Row>
          )))
        )}
        {connectHint}
      </Card>

      {/* ------------------------- surplus auctions ------------------------- */}
      <Card>
        <SectionTitle>Surplus auctions — profits burn TKR</SectionTitle>
        <SectionBlurb>
          Once system profits exceed the retained buffer, anyone can put a {formatNumber(a.vow.bump, 0)}-JDAI
          slice up for auction. Bidders compete in TKR (each bid ≥5% higher); the winner takes the
          JDAI and their TKR is destroyed forever.
        </SectionBlurb>
        {a.flap.auctions.length === 0 ? (
          <Quiet>
            No surplus auctions.{' '}
            {a.vow.humpCold
              ? 'The trigger is deliberately parked out of reach while the system is being stood up — profits are accumulating in the Vow untouched.'
              : a.flap.reachable
                ? 'The surplus is over the trigger — anyone can start one.'
                : `Surplus (${formatNumber(a.vow.surplus, 2)} JDAI) is below the trigger (buffer ${formatNumber(a.vow.hump, 0)} + lot ${formatNumber(a.vow.bump, 0)} JDAI).`}
          </Quiet>
        ) : (
          a.flap.auctions.map((auc) => (
            <Row key={auc.id}>
              <div className="info">
                <span className="id">#{auc.id}</span>{' '}
                {formatNumber(Number(ethers.formatUnits(auc.lot, 45)), 2)} JDAI lot
                {' '}· top bid {formatNumber(Number(ethers.formatUnits(auc.bid, 18)), 4)} TKR
                {' '}by {truncateAddress(auc.guy)}
                {' '}· <span className="dim">{finished(auc) ? 'settleable'
                  : expiredNoBids(auc) ? 'expired with no bids — reopen it'
                  : `closes in ${countdown(auc.tic !== 0 ? Math.min(auc.tic, auc.end) : auc.end)}`}</span>
              </div>
              <div className="actions">
                {account && (finished(auc) ? (
                  <Button onClick={() => flapDeal(auc)}>Settle (burns TKR)</Button>
                ) : expiredNoBids(auc) ? (
                  <Button onClick={() => tick('flap', auc)}>Reopen auction</Button>
                ) : (
                  <>
                    <Mini placeholder="TKR bid" value={inputs[`flap${auc.id}`] || ''}
                      onChange={(e) => setIn(`flap${auc.id}`, e.target.value)} />
                    <Button onClick={() => tend(auc)}
                      disabled={!inputs[`flap${auc.id}`] || !a.user
                        || parseFloat(inputs[`flap${auc.id}`] || '0') > a.user.tkrBalNum}>
                      Bid TKR
                    </Button>
                  </>
                ))}
              </div>
            </Row>
          ))
        )}
        {account && a.flap.reachable && (
          <div style={{ marginTop: '1rem' }}>
            <Button onClick={kickFlap}>Start surplus auction ({formatNumber(a.vow.bump, 0)} JDAI lot)</Button>
          </div>
        )}
        {connectHint}
      </Card>

      {/* --------------------------- debt auctions -------------------------- */}
      <Card>
        <SectionTitle>Debt auctions — bad debt mints TKR</SectionTitle>
        <SectionBlurb>
          If a liquidation shortfall ever exceeds the surplus, the system sells freshly minted
          TKR for {formatNumber(a.vow.sump, 0)} JDAI at a time. Bidders compete by accepting
          LESS TKR for that JDAI; the winner&apos;s payment cancels the bad debt.
        </SectionBlurb>
        {a.flop.auctions.length === 0 ? (
          <Quiet>
            No debt auctions{a.flop.reachable ? ' — but the books qualify: anyone can start one.'
              : ` — none are possible: they require over ${formatNumber(a.vow.sump, 0)} JDAI of
              uncovered bad debt after all surplus is used, and liquidations (the only source
              of bad debt) are ${a.armed ? 'active' : 'switched off'}.`}
          </Quiet>
        ) : (
          a.flop.auctions.map((auc) => (
            <Row key={auc.id}>
              <div className="info">
                <span className="id">#{auc.id}</span>{' '}
                pays {formatNumber(Number(ethers.formatUnits(auc.bid, 45)), 2)} JDAI
                {' '}· current ask {formatNumber(Number(ethers.formatUnits(auc.lot, 18)), 2)} TKR
                {' '}· <span className="dim">{finished(auc) ? 'settleable'
                  : expiredNoBids(auc) ? 'expired with no bids — reopening raises the TKR lot 50%'
                  : `closes in ${countdown(auc.tic !== 0 ? Math.min(auc.tic, auc.end) : auc.end)}`}</span>
              </div>
              <div className="actions">
                {account && (finished(auc) ? (
                  <Button onClick={() => flopDeal(auc)}>Settle (mints TKR)</Button>
                ) : expiredNoBids(auc) ? (
                  <Button onClick={() => tick('flop', auc)}>Reopen auction</Button>
                ) : (
                  <>
                    <Mini placeholder="TKR asked" value={inputs[`flop${auc.id}`] || ''}
                      onChange={(e) => setIn(`flop${auc.id}`, e.target.value)} />
                    <Button onClick={() => dent(auc)}
                      disabled={!inputs[`flop${auc.id}`] || !a.user || a.user.vatDai < auc.bid}>
                      Bid
                    </Button>
                  </>
                ))}
              </div>
            </Row>
          ))
        )}
        {account && a.flop.reachable && (
          <div style={{ marginTop: '1rem' }}>
            <Button onClick={kickFlop}>Start debt auction</Button>
          </div>
        )}
        {connectHint}
      </Card>
    </Wrap>
  );
};

export default AuctionsPage;
